import { Controller, FieldError, Path, useFormContext } from "react-hook-form"
import { BookFlightFormValues } from "../../model"
import { useAppSelector } from "@/shared/lib/redux"
import { selectCountries, selectCountriesLoading } from "@/entities/aviabooking-reference"
import Select from 'react-select'
import { ISavedDocument, PassengerType, TPassengerType, DocumentType, GenderType } from "@/shared/api/aviabooking"
import { FC, memo, useCallback, useEffect, useMemo, useState } from "react"
import { Modal } from "@/shared/ui/modal"
import { deleteSavedDocumentThunk } from "@/entities/aviabooking-saved-documents"
import { useAppDispatch } from "@/shared/lib/redux"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { ErrorFieldWrapper } from "../error-field-wrapper"
import { SeatMap } from "../seat-map"

interface DateInputProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    error?: string
    id: string
    disabled?: boolean
}

interface CustomSelectProps {
    value: any
    onChange: (value: any) => void
    noOptionsMessage?: () => React.ReactNode
    options: Array<{ value: any, label: string }>
    placeholder: string
    error?: string
    id: string
    isLoading?: boolean
    isDisabled?: boolean
    isClearable?: boolean
}

const DateInput = memo(({ value, onChange, placeholder, error, id, disabled = false }: DateInputProps) => {
    const formatDate = (input: string): string => {
        const numbers = input.replace(/\D/g, '')
        const limited = numbers.slice(0, 8)

        if (limited.length <= 2) {
            return limited
        } else if (limited.length <= 4) {
            return `${limited.slice(0, 2)}.${limited.slice(2)}`
        } else {
            return `${limited.slice(0, 2)}.${limited.slice(2, 4)}.${limited.slice(4)}`
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatDate(e.target.value)
        onChange(formatted)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab'].includes(e.key)) {
            return
        }

        if (!/\d/.test(e.key)) {
            e.preventDefault()
        }
    }

    return (
        <div className="h-full">
            <ErrorFieldWrapper error={error}>
                <input
                    id={id}
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    type="text"
                    className={`rounded-lg px-3 py-4 w-full focus:bg-white transition border bg-slate-50 placeholder:text-zinc-400 ${error
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                        }`}
                />
            </ErrorFieldWrapper>
        </div>
    )
})

interface DocumentNumberInputProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    error?: string
    id: string
    type: DocumentType
    disabled?: boolean
}

const DOCUMENT_REGEX: Record<DocumentType, RegExp> = {
    [DocumentType.BerthRegDocument]: /^[IVX]{0,2}[А-Я]{0,2}\d{0,6}$/,
    [DocumentType.Passport]: /^[0-9]{0,10}$/,
    [DocumentType.InternationalPassportRU]: /^[0-9]{0,9}$/,
    [DocumentType.Unspecified]: /.^/
}

const removeFormatting = (value: string): string => value.replace(/[\s-]/g, '')

const formatBirthCertificate = (value: string): string => {
    const limited = value.slice(0, 10)
    return limited.length <= 4 ? limited : `${limited.slice(0, 4)}-${limited.slice(4, 10)}`
}

const formatPassport = (value: string): string => {
    const limited = value.slice(0, 10)
    if (limited.length <= 6) {
        return limited
    } else if (limited.length <= 8) {
        return `${limited.slice(0, 6)} ${limited.slice(6, 8)}`
    }
    return `${limited.slice(0, 6)} ${limited.slice(6, 8)} ${limited.slice(8, 10)}`
}

const formatInternationalPassport = (value: string): string => {
    const limited = value.slice(0, 9)
    return limited.length <= 7 ? limited : `${limited.slice(0, 7)} ${limited.slice(7, 9)}`
}

const formatDocument = (value: string, type: DocumentType): string => {
    const rawValue = removeFormatting(value)

    switch (type) {
        case DocumentType.BerthRegDocument:
            return formatBirthCertificate(rawValue)
        case DocumentType.Passport:
            return formatPassport(rawValue)
        case DocumentType.InternationalPassportRU:
            return formatInternationalPassport(rawValue)
        default:
            return rawValue
    }
}

const DocumentNumberInput = memo(({
    value,
    onChange,
    placeholder,
    error,
    id,
    type,
    disabled = false
}: DocumentNumberInputProps) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = removeFormatting(e.target.value)
        const regex = DOCUMENT_REGEX[type]

        if (regex.test(rawValue)) {
            onChange(rawValue)
        }
    }

    const displayValue = formatDocument(value, type)

    return (
        <div className="h-full">
            <ErrorFieldWrapper error={error}>
                <input
                    id={id}
                    value={displayValue}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    type="text"
                    className={`rounded-lg px-3 py-4 w-full focus:bg-white transition border bg-slate-50 placeholder:text-zinc-400 ${error
                            ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                            : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                        }`}
                />
            </ErrorFieldWrapper>
        </div>
    )
})

const CustomSelect = memo(({
    value,
    noOptionsMessage,
    onChange,
    options,
    placeholder,
    error,
    id,
    isLoading = false,
    isDisabled = false,
    isClearable = false
}: CustomSelectProps) => {
    const selectStyles = {
        control: (base: any) => ({
            ...base,
            border: '0',
            padding: '3px 0',
            outline: '0',
            width: '100%',
            borderColor: 'transparent',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            minHeight: 'auto'
        }),
        valueContainer: (base: any) => ({
            ...base,
            border: '0',
            outline: '0',
            padding: '0',
        }),
        menu: (base: any) => ({
            ...base,
            left: '0',
            zIndex: 50,
        }),
        indicatorsContainer: (base: any) => ({
            ...base,
            padding: '0',
        }),
        dropdownIndicator: (base: any) => ({
            ...base,
            padding: '0',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
    }

    return (
        <div className="h-full">
            <ErrorFieldWrapper error={error}>
                <Select
                    inputId={id}
                    value={options.find(opt => opt.value === value)}
                    onChange={(selectedOption) => onChange(selectedOption?.value)}
                    noOptionsMessage={noOptionsMessage}
                    options={options}
                    placeholder={placeholder}
                    isLoading={isLoading}
                    isDisabled={isDisabled}
                    isClearable={isClearable}
                    styles={selectStyles}
                    classNamePrefix="react_select_custom"
                    className={`rounded-lg px-3 flex items-center py-[9px] w-full focus:bg-white transition border bg-slate-50 placeholder:text-zinc-400 ${error
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                        : 'border-gray-300 focus:ring-2 focus:ring-blue-200'
                        }`}
                />
            </ErrorFieldWrapper>
        </div>
    )
})

const GENDER_OPTIONS = [
    { label: 'Мужской', value: GenderType.Male },
    { label: 'Женский', value: GenderType.Female },
]

const getDocumentOptions = (type: TPassengerType) => {
    switch (type) {
        case PassengerType.ADT:
            return [
                { value: DocumentType.Passport, label: "Паспорт РФ" },
                { value: DocumentType.InternationalPassportRU, label: "Заграничный паспорт" },
            ]
        case PassengerType.INF:
            return [
                { value: DocumentType.BerthRegDocument, label: "Свидетельство о рождении" },
            ]
        case PassengerType.CNN:
            return [
                { value: DocumentType.Passport, label: "Паспорт РФ" },
                { value: DocumentType.InternationalPassportRU, label: "Заграничный паспорт" },
                { value: DocumentType.BerthRegDocument, label: "Свидетельство о рождении" },
            ]
        default:
            return []
    }
}

const getPassengerTypeLabel = (type: TPassengerType): string => {
    switch (type) {
        case PassengerType.ADT: return 'Взрослый'
        case PassengerType.CNN: return 'Ребенок'
        case PassengerType.INF: return 'Младенец (без места)'
        default: return 'Пассажир'
    }
}

interface PassengerInfoItemProps {
    traveller: BookFlightFormValues['travellers'][number]
    index: number
    savedDocuments: ISavedDocument[]
    savedDocumentsLoading: boolean
}

const getDocumentTypeLabel = (type: DocumentType): string => {
    switch (type) {
        case DocumentType.Passport:
            return "Паспорт РФ"
        case DocumentType.InternationalPassportRU:
            return "Заграничный паспорт"
        case DocumentType.BerthRegDocument:
            return "Свидетельство о рождении"
        default:
            return type
    }
}

const formatSavedFio = (saved: ISavedDocument): string =>
    [saved.last_name, saved.first_name, saved.middle_name].filter(Boolean).join(' ')

export const PassengerInfoItem: FC<PassengerInfoItemProps> = ({ traveller, index, savedDocuments, savedDocumentsLoading }) => {
    const dispatch = useAppDispatch()
    const countries = useAppSelector(selectCountries)
    const countriesLoading = useAppSelector(selectCountriesLoading)

    // No seat select for child without place
    const selectSeatAllowed = traveller.type !== PassengerType.INF
    const [showSeatMap, setShowSeatMap] = useState<boolean>(false)

    const countriesSelectOptions = useMemo(() =>
        countries.map((country) => ({
            value: country.code,
            label: country.name
        })),
        [countries]
    )
    const documentOptions = useMemo(() => getDocumentOptions(traveller.type), [traveller.type])
    const allowedDocumentTypes = useMemo(() => documentOptions.map(({ value }) => value), [documentOptions])
    const filteredSavedDocuments = useMemo(
        () => savedDocuments.filter((saved) => allowedDocumentTypes.includes(saved.id_document.type)),
        [allowedDocumentTypes, savedDocuments]
    )
    const previewSavedDocuments = useMemo(() => filteredSavedDocuments.slice(0, 2), [filteredSavedDocuments])

    const fieldIdPrefix = `traveller-${traveller.id}`

    const {
        watch,
        control,
        register,
        setValue,
        formState: { errors },
    } = useFormContext<BookFlightFormValues>()

    const getFieldError = (index: number, fieldName: Path<Omit<BookFlightFormValues['travellers'][number], 'selected_seat'>>) => {
        const error = errors.travellers?.[index]?.[fieldName]
        return error ? (error as FieldError).message : undefined
    }

    const [savedProfilePicker, setSavedProfilePicker] = useState<string>("")
    const [savedListModalOpen, setSavedListModalOpen] = useState(false)
    const [savedListSearch, setSavedListSearch] = useState("")
    const [deletingId, setDeletingId] = useState<string | null>(null)

    const applySavedDocument = useCallback(
        (saved: ISavedDocument) => {
            setSavedProfilePicker(saved.id)
            setValue(`travellers.${index}.first_name`, saved.first_name)
            setValue(`travellers.${index}.last_name`, saved.last_name)
            setValue(`travellers.${index}.middle_name`, saved.middle_name ?? "")
            setValue(`travellers.${index}.date_of_birth`, saved.date_of_birth)
            setValue(`travellers.${index}.nationality`, saved.nationality)
            setValue(`travellers.${index}.gender`, saved.gender)
            setValue(`travellers.${index}.document_type`, saved.id_document.type)
            setValue(`travellers.${index}.document_number`, saved.id_document.number)
            setValue(`travellers.${index}.issue_country_code`, saved.id_document.issue_country_code)
            setValue(`travellers.${index}.expired_at`, saved.id_document.expired_at ?? "")
            setValue(`travellers.${index}.save_document`, false)
        },
        [index, setValue]
    )

    const modalListFiltered = useMemo(() => {
        const q = savedListSearch.trim().toLowerCase()
        if (!q) return filteredSavedDocuments
        return filteredSavedDocuments.filter((saved) => {
            const fio = formatSavedFio(saved).toLowerCase()
            const docNum = saved.id_document.number.toLowerCase()
            return fio.includes(q) || docNum.includes(q)
        })
    }, [filteredSavedDocuments, savedListSearch])

    const handleDeleteSaved = async (documentId: string) => {
        setDeletingId(documentId)
        try {
            await dispatch(deleteSavedDocumentThunk(documentId)).unwrap()
            notify.success("Пассажир удалён из сохранённых")
            if (savedProfilePicker === documentId) {
                setSavedProfilePicker("")
            }
        } catch (e) {
            notify.error(getErrorMessage(e as { error_id?: string }))
        } finally {
            setDeletingId(null)
        }
    }

    useEffect(() => {
        setSavedProfilePicker("")
    }, [traveller.id])

    useEffect(() => {
        if (savedProfilePicker && !savedDocuments.some((d) => d.id === savedProfilePicker)) {
            setSavedProfilePicker("")
        }
    }, [savedDocuments, savedProfilePicker])

    return (
        <div key={traveller.id} className="flex flex-col gap-4">
            {traveller.type !== PassengerType.ADT && (
                <div
                    className="px-4 py-6 bg-blue-50 rounded-3xl border border-blue-100"
                >
                    <p className="text-blue-800 text-sm">
                        Согласно правилам авиакомпании, сопровождать ребёнка до двенадцати лет может только взрослый старше 18-ти лет.
                    </p>
                </div>
            )}

            <section
                className="p-4 bg-white rounded-3xl border border-gray-200"
            >
                <h3 id={`${fieldIdPrefix}-title`} className="font-bold mb-4 text-lg">
                    {getPassengerTypeLabel(traveller.type)}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-12">
                        <div className="block text-sm font-medium text-gray-700 mb-2">
                            Сохранённые пассажиры
                        </div>
                        {savedDocumentsLoading ? (
                            <div className="rounded-lg border border-gray-200 bg-slate-50 px-4 py-6 text-sm text-gray-500">
                                Загрузка списка…
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {previewSavedDocuments.map((saved) => {
                                    const selected = savedProfilePicker === saved.id
                                    return (
                                        <button
                                            key={saved.id}
                                            type="button"
                                            onClick={() => applySavedDocument(saved)}
                                            className={`text-left rounded-2xl border px-4 py-4 transition hover:bg-slate-50 ${
                                                selected
                                                    ? 'border-color-primary bg-color-primary/5 shadow-sm'
                                                    : 'border-gray-200 bg-white'
                                            }`}
                                        >
                                            <div className="font-semibold text-gray-900 leading-snug">
                                                {formatSavedFio(saved)}
                                            </div>
                                            <div className="mt-1 text-sm text-gray-500">
                                                {saved.date_of_birth}
                                            </div>
                                        </button>
                                    )
                                })}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSavedListSearch("")
                                        setSavedListModalOpen(true)
                                    }}
                                    className="text-left rounded-2xl border border-gray-300 px-4 py-4 transition hover:border-color-primary hover:bg-slate-50"
                                >
                                    <div className="font-semibold text-gray-900">
                                        Посмотреть все
                                    </div>
                                    <div className="mt-1 text-sm text-gray-500">
                                        список и управление данными
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>

                    <Modal
                        isOpen={savedListModalOpen}
                        onRequestClose={() => setSavedListModalOpen(false)}
                        title="Сохранённые пассажиры"
                    >
                        <div className="flex flex-col gap-4 min-w-0 w-full max-w-xl">
                            <input
                                type="search"
                                value={savedListSearch}
                                onChange={(e) => setSavedListSearch(e.target.value)}
                                placeholder="Поиск"
                                className="rounded-lg px-3 py-3 w-full border border-gray-300 outline-none"
                            />
                            {modalListFiltered.length === 0 ? (
                                <p className="text-sm text-gray-500 py-2">
                                    {filteredSavedDocuments.length === 0
                                        ? 'Нет сохранённых документов для этого типа пассажира'
                                        : 'Ничего не найдено'}
                                </p>
                            ) : (
                                <ul className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto pr-1">
                                    {modalListFiltered.map((saved) => (
                                        <li
                                            key={saved.id}
                                            className="flex items-stretch gap-2 rounded-xl border border-gray-200 overflow-hidden bg-white"
                                        >
                                            <button
                                                type="button"
                                                className="flex-1 text-left px-4 py-3 hover:bg-slate-50 transition min-w-0"
                                                onClick={() => {
                                                    applySavedDocument(saved)
                                                    setSavedListModalOpen(false)
                                                }}
                                            >
                                                <div className="font-medium text-gray-900 truncate">
                                                    {formatSavedFio(saved)}
                                                </div>
                                                <div className="text-sm text-gray-400">
                                                    {saved.date_of_birth}
                                                    {' | '}
                                                    {getDocumentTypeLabel(saved.id_document.type)}
                                                    {' | '}
                                                    {formatDocument(saved.id_document.number, saved.id_document.type)}
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                disabled={deletingId === saved.id}
                                                onClick={() => handleDeleteSaved(saved.id)}
                                                className="shrink-0 px-3 text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                                            >
                                                {deletingId === saved.id ? 'Удаление' : 'Удалить'}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </Modal>

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-last_name`} className="block text-sm font-medium text-gray-700 mb-1">
                            Фамилия *
                        </label>
                        <ErrorFieldWrapper error={getFieldError(index, 'last_name')}>
                            <input
                                {...register(`travellers.${index}.last_name`)}
                                id={`${fieldIdPrefix}-last_name`}
                                placeholder="IVANOV"
                                type="text"
                                className={`rounded-lg px-3 py-4 w-full focus:bg-white transition border bg-slate-50 placeholder:text-zinc-400 ${getFieldError(index, 'last_name')
                                    ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                                    : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                                    }`}
                            />
                        </ErrorFieldWrapper>
                    </div>

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-first_name`} className="block text-sm font-medium text-gray-700 mb-1">
                            Имя *
                        </label>
                        <ErrorFieldWrapper error={getFieldError(index, 'first_name')}>
                            <input
                                {...register(`travellers.${index}.first_name`)}
                                id={`${fieldIdPrefix}-first_name`}
                                placeholder="IVAN"
                                type="text"
                                className={`rounded-lg px-3 py-4 w-full focus:bg-white transition border bg-slate-50 placeholder:text-zinc-400 ${getFieldError(index, 'first_name')
                                    ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                                    : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                                    }`}
                            />
                        </ErrorFieldWrapper>
                    </div>

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-middle_name`} className="block text-sm font-medium text-gray-700 mb-1">
                            Отчество
                        </label>
                        <ErrorFieldWrapper error={getFieldError(index, 'middle_name')}>
                            <input
                                {...register(`travellers.${index}.middle_name`)}
                                id={`${fieldIdPrefix}-middle_name`}
                                placeholder="IVANOVICH"
                                type="text"
                                className={`rounded-lg px-3 py-4 w-full focus:bg-white transition border bg-slate-50 placeholder:text-zinc-400 ${getFieldError(index, 'middle_name')
                                    ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                                    : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                                    }`}
                            />
                        </ErrorFieldWrapper>
                    </div>

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-gender`} className="block text-sm font-medium text-gray-700 mb-1">
                            Пол *
                        </label>
                        <Controller
                            name={`travellers.${index}.gender`}
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    id={`${fieldIdPrefix}-gender`}
                                    value={field.value}
                                    onChange={field.onChange}
                                    options={GENDER_OPTIONS}
                                    placeholder="Выберите пол"
                                    error={getFieldError(index, 'gender')}
                                />
                            )}
                        />
                    </div>

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-date_of_birth`} className="block text-sm font-medium text-gray-700 mb-1">
                            Дата рождения *
                        </label>
                        <Controller
                            name={`travellers.${index}.date_of_birth`}
                            control={control}
                            render={({ field }) => (
                                <DateInput
                                    id={`${fieldIdPrefix}-date_of_birth`}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="ДД.ММ.ГГГГ"
                                    error={getFieldError(index, 'date_of_birth')}
                                />
                            )}
                        />
                    </div>

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-nationality`} className="block text-sm font-medium text-gray-700 mb-1">
                            Гражданство *
                        </label>
                        <Controller
                            name={`travellers.${index}.nationality`}
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    id={`${fieldIdPrefix}-nationality`}
                                    value={field.value}
                                    onChange={field.onChange}
                                    options={countriesSelectOptions}
                                    placeholder="Выберите гражданство"
                                    error={getFieldError(index, 'nationality')}
                                    isLoading={countriesLoading}
                                />
                            )}
                        />
                    </div>

                    <div className="h-px bg-gray-200 md:col-span-12" />

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-document_type`} className="block text-sm font-medium text-gray-700 mb-1">
                            Тип документа *
                        </label>
                        <Controller
                            name={`travellers.${index}.document_type`}
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    id={`${fieldIdPrefix}-document_type`}
                                    value={field.value}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        setValue(`travellers.${index}.document_number`, "")
                                        setValue(`travellers.${index}.expired_at`, "")
                                    }}
                                    options={documentOptions}
                                    placeholder="Выберите тип документа"
                                    error={getFieldError(index, 'document_type')}
                                />
                            )}
                        />
                    </div>

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-document_number`} className="block text-sm font-medium text-gray-700 mb-1">
                            Номер документа *
                        </label>
                        <Controller
                            name={`travellers.${index}.document_number`}
                            control={control}
                            render={({ field }) => (
                                <DocumentNumberInput
                                    id={`${fieldIdPrefix}-document_number`}
                                    value={field.value}
                                    onChange={field.onChange}
                                    type={watch(`travellers.${index}.document_type`)}
                                    placeholder="Номер документа"
                                    error={getFieldError(index, 'document_number')}
                                />
                            )}
                        />
                    </div>

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-expired_at`} className="block text-sm font-medium text-gray-700 mb-1">
                            {watch(`travellers.${index}.document_type`) === DocumentType.Passport ? 'Дата выдачи *' : 'Годен до *'}
                        </label>
                        <Controller
                            name={`travellers.${index}.expired_at`}
                            control={control}
                            render={({ field }) => (
                                <DateInput
                                    id={`${fieldIdPrefix}-expired_at`}
                                    value={field.value}
                                    disabled={watch(`travellers.${index}.document_type`) === DocumentType.BerthRegDocument}
                                    onChange={field.onChange}
                                    placeholder="ДД.ММ.ГГГГ"
                                    error={getFieldError(index, 'expired_at')}
                                />
                            )}
                        />
                    </div>

                    <div className="md:col-span-4">
                        <label htmlFor={`${fieldIdPrefix}-issue_country_code`} className="block text-sm font-medium text-gray-700 mb-1">
                            Выдан в *
                        </label>
                        <Controller
                            name={`travellers.${index}.issue_country_code`}
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    id={`${fieldIdPrefix}-issue_country_code`}
                                    value={field.value}
                                    onChange={field.onChange}
                                    options={countriesSelectOptions}
                                    placeholder="Выберите страну"
                                    error={getFieldError(index, 'issue_country_code')}
                                    isLoading={countriesLoading}
                                />
                            )}
                        />
                    </div>

                    <div className="md:col-span-12">
                        <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                            <input
                                type="checkbox"
                                {...register(`travellers.${index}.save_document`)}
                                className="h-4 w-4 rounded border border-gray-300"
                            />
                            Сохранить профиль для будущих бронирований
                        </label>
                    </div>
                </div>

                {selectSeatAllowed &&
                    <div>
                        <div className="mt-4 border-t mb-4" />

                        <button
                            type="button"
                            onClick={() => setShowSeatMap((prev) => !prev)}
                            className="flex items-center justify-between w-full p-4 bg-slate-50 hover:bg-white rounded-lg border border-gray-300 transition-all duration-300 hover:shadow-md"
                        >
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-color-primary rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10h6m-6 4h6m-6 4h6" />
                                    </svg>
                                </div>
                                <div className="text-left">
                                    <h3 className="font-semibold text-gray-800">Выбор места</h3>
                                    <p className="text-sm text-gray-600">Выберите место в салоне самолета</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500">
                                    {showSeatMap ? 'Скрыть карту' : 'Показать карту'}
                                </span>
                                <svg
                                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${showSeatMap ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </button>
                        {showSeatMap &&
                            <div className="mt-4">
                                <SeatMap travellerIndex={index} />
                            </div>
                        }
                    </div>
                }
            </section>
        </div>
    )
}
